import type { FileFormat } from '@/types/file-format';
import type { VideoResolution as VideoResolutionPreference } from '@/types/video-resolution';
import type { Permission, Role } from '@/types/auth';

/**
 * Public types for the Download Gateway.
 *
 * The gateway answers one question: "given user state and this asset, what UI
 * should I show?" — returning a discriminated `DownloadDecision`. Callers
 * switch on `decision.action` and dispatch the appropriate UI action.
 */

export type AudioFormat = 'mp3' | 'wav';
export type VideoResolution = '720p' | '1080p' | '4k';

/**
 * Stock-video resolutions in ascending order. Shared source of truth for
 * the evaluator (computing `lockedAbove` cutoffs) and the dialog (rendering
 * the toggle-card list). Adding a future resolution updates both at once.
 */
export const RESOLUTION_ORDER: readonly VideoResolution[] = [
  '720p',
  '1080p',
  '4k',
];

export type AssetRef =
  | {
      kind: 'music' | 'sfx' | 'mg' | 'lut';
      id: number;
      isPremium: boolean;
    }
  | {
      kind: 'stock-video';
      id: number;
      isPremium: boolean;
      // Permission-driven tier classification for the resolution chooser.
      //
      // Goodwill grants of individual permissions can elevate a Free user
      // for *any* asset kind — that's just how the backend's per-permission
      // model works. For audio / MG / LUT it doesn't matter, because every
      // decision is a single permission check (`has_premium_music?`,
      // `has_unlimited?`, `has_wav?`) and the answers compose into a
      // decision without needing a tier summary.
      //
      // Stock video is different because the chooser has a *graded* lock
      // state: 720p / 1080p / 4k need to be locked together up to the
      // user's cutoff, and the cutoff is a conjunction of four perms
      // (4k AND 1080p AND unlimited AND premium for Pro; the same minus
      // 4k for Creator). The hook collapses that conjunction once and
      // caches the result here so the evaluator can derive `lockedAbove`
      // without re-checking individual perms.
      bundle: 'pro' | 'creator' | null;
    };

export type RequestedFormat =
  | { type: 'audio'; format: AudioFormat }
  | { type: 'video'; resolution: VideoResolution }
  | undefined;

export type FormatChoice =
  | {
      type: 'audio';
      options: readonly AudioFormat[];
      wavLocked: boolean;
    }
  | {
      type: 'video';
      options: readonly VideoResolution[];
      lockedAbove: '720p' | '1080p' | null;
    };

export type ResolvedFormat =
  | { type: 'audio'; format: AudioFormat }
  | { type: 'video'; resolution: VideoResolution }
  | { type: 'file' };

export type NavigateTarget =
  | 'sign-up'
  | 'login'
  | 'verify-email'
  | 'overdue-payment'
  | 'pause-warning';

export type UpsellReason =
  | 'premium-asset'
  | 'no-credits'
  | 'format-locked'
  | 'resolution-locked';

export type UpsellTargetPlan = 'Creator' | 'Pro';

export type DownloadDecision =
  | { action: 'pending' }
  | {
      action: 'download';
      format: ResolvedFormat;
      /**
       * True when the modal should land the user on a confirmation screen
       * before kicking off the download. Covers three cases:
       *   - audio with credits but no credit-usage acknowledgement
       *   - motion-graphics file downloads (always show the file-info screen)
       *   - LUT downloads outside the creator-tier shortcut
       * False when the download should start immediately (`directDownload`).
       */
      requiresConfirmation: boolean;
    }
  | { action: 'ask-format'; choice: FormatChoice }
  | {
      action: 'navigate';
      to: NavigateTarget;
      welcomeMessageType?: 'download' | 'premium-download';
    }
  | {
      action: 'upsell';
      reason: UpsellReason;
      targetPlan: UpsellTargetPlan;
      // Describes the user's credit pool, not the reason for the upsell.
      // True only when the user has no unlimited grant AND no credits
      // remaining — drives the "0 downloads left" copy in the upsell modal.
      // Distinct from `reason`: a user can hit a `premium-asset` upsell
      // while still having plenty of credits.
      hasNoDownloadsLeft: boolean;
    };

/**
 * Plain data input to `evaluateDownload`. Composed from existing hooks/atoms
 * by `useGatewaySnapshot` — passed verbatim into the pure function for tests.
 */
export interface GatewaySnapshot {
  credits: number;
  creditUsageAcknowledged: boolean;
  formatPreference: FileFormat | undefined;
  /** False until `useSettings` has hydrated. */
  isSettingsLoaded: boolean;
  overduePayment: boolean;

  /**
   * Permission strings the user holds. Only the subset relevant to downloads
   * needs to be present — `evaluateDownload` checks via `permissions.has(...)`.
   */
  permissions: ReadonlySet<Permission>;

  /**
   * The user's saved stock-video resolution from account preferences.
   * `AskEveryTime` (or unset) opens the chooser; anything else downloads
   * straight away at that resolution, clamped to their tier (DEV-6677).
   */
  resolutionPreference: VideoResolutionPreference | undefined;

  /**
   * Role strings the user holds. Used for tier-classification only — the
   * free-user email-verification gate (paying tiers skip it), the LUT
   * creator-tier confirmation-skip, the stock-video plan-role fallback,
   * and the upsell-payload account-type projection. Capability gating
   * (can the user actually download X?) is permission-driven — see
   * `apps/uppbeat-frontend/CLAUDE.md` → "Auth: Permissions vs Roles".
   */
  roles: ReadonlySet<Role>;
  /** Subscription is paused — downloads are gated behind resuming. */
  subscriptionPaused: boolean;

  user: {
    isAuthenticated: boolean;
    isEmailVerified: boolean;
    isReturningUser: boolean;
  };
}

export interface GatewayInput {
  asset: AssetRef;
  requested?: RequestedFormat;
}
